import React, { useState, useRef, useCallback } from 'react';
import {
  X, Upload, Loader2, FileText, CheckCircle2, AlertCircle,
  ChevronLeft, ChevronRight, Trash2, UserPlus, Sparkles,
} from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { createCandidate, parseResumeSingle } from '../api';
import { Position, EDUCATION_OPTIONS, SOURCE_OPTIONS } from '../types';

interface Props {
  positions: Position[];
  onClose: () => void;
  onCreated: () => void;
}

interface ParsedCandidate {
  name: string;
  phone: string;
  email: string;
  gender: string;
  age: string;
  latest_company: string;
  latest_title: string;
  work_experiences: { company: string; title: string }[];
  project_experiences: string[];
  education: string;
  school: string;
  major: string;
  work_years: string;
  expected_salary: string;
  expected_city: string;
  source: string;
  portfolio_url: string;
  position_id: string;
  notes: string;
  resume_file: string;
  resume_text: string;
  _fileName: string;
  _status: 'parsing' | 'success' | 'error' | 'saving' | 'saved';
  _error?: string;
}

const emptyForm = (): ParsedCandidate => ({
  name: '', phone: '', email: '', gender: '', age: '',
  latest_company: '', latest_title: '', work_experiences: [], project_experiences: [], education: '', school: '', major: '',
  work_years: '', expected_salary: '', expected_city: '', source: '',
  portfolio_url: '', position_id: '', notes: '', resume_file: '', resume_text: '',
  _fileName: '', _status: 'success', _error: undefined,
});

type Step = 'upload' | 'review';

export default function AddCandidateModal({ positions, onClose, onCreated }: Props) {
  const [step, setStep] = useState<Step>('upload');
  const [candidates, setCandidates] = useState<ParsedCandidate[]>([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // --- Upload & Parse ---
  const handleFiles = useCallback(async (files: FileList | File[]) => {
    const fileArr = Array.from(files).filter(f =>
      f.type === 'application/pdf' ||
      f.type === 'application/msword' ||
      f.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    );
    if (fileArr.length === 0) {
      toast.error('请上传 PDF 或 Word 文档');
      return;
    }

    // Create placeholder entries
    const startIdx = candidates.length;
    const placeholders: ParsedCandidate[] = fileArr.map(f => ({
      ...emptyForm(),
      _fileName: f.name,
      _status: 'parsing',
    }));

    setCandidates(prev => [...prev, ...placeholders]);
    setStep('review');
    setActiveIdx(startIdx);

    // Parse each file sequentially — backend auto-detects portfolio files
    for (let i = 0; i < fileArr.length; i++) {
      const idx = startIdx + i;
      try {
        const result = await parseResumeSingle(fileArr[i]);
        const parsed = result.parsed;

        if (result.isPortfolio) {
          // Portfolio file: just store the file URL, no candidate info to extract
          setCandidates(prev => {
            const updated = [...prev];
            updated[idx] = {
              ...emptyForm(),
              portfolio_url: parsed.portfolio_url || '',
              _fileName: fileArr[i].name,
              _status: 'success',
            };
            return updated;
          });
          toast(`🎨 作品集已上传: ${fileArr[i].name}`, { icon: '📁' });
        } else {
          setCandidates(prev => {
            const updated = [...prev];
            updated[idx] = {
              name: parsed.name || '',
              phone: parsed.phone || '',
              email: parsed.email || '',
              gender: parsed.gender || '',
              age: parsed.age ? String(parsed.age) : '',
              latest_company: parsed.latest_company || '',
              latest_title: parsed.latest_title || '',
              work_experiences: parsed.work_experiences || [],
              project_experiences: parsed.project_experiences || [],
              education: parsed.education || '',
              school: parsed.school || '',
              major: parsed.major || '',
              work_years: parsed.work_years ? String(parsed.work_years) : '',
              expected_salary: parsed.expected_salary || '',
              expected_city: parsed.expected_city || '',
              source: parsed.source || '',
              portfolio_url: parsed.portfolio_url || '',
              position_id: '',
              notes: parsed.notes || '',
              resume_file: parsed.resume_file || '',
              resume_text: parsed.resume_text || '',
              _fileName: fileArr[i].name,
              _status: 'success',
            };
            return updated;
          });
        }
      } catch (err: any) {
        setCandidates(prev => {
          const updated = [...prev];
          updated[idx] = {
            ...updated[idx],
            _status: 'error',
            _error: err?.response?.data?.message || err?.message || '解析失败',
          };
          return updated;
        });
      }
    }
  }, [candidates.length]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  }, [handleFiles]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => setDragOver(false), []);

  // --- Manual add (no file) ---
  const handleAddManual = () => {
    setCandidates(prev => [...prev, emptyForm()]);
    setActiveIdx(candidates.length);
    setStep('review');
  };

  // --- Field change ---
  const handleChange = (field: string, value: string) => {
    setCandidates(prev => {
      const updated = [...prev];
      updated[activeIdx] = { ...updated[activeIdx], [field]: value };
      return updated;
    });
  };

  // --- Remove candidate from list ---
  const handleRemove = (idx: number) => {
    setCandidates(prev => prev.filter((_, i) => i !== idx));
    if (activeIdx >= candidates.length - 1) {
      setActiveIdx(Math.max(0, candidates.length - 2));
    } else if (idx < activeIdx) {
      setActiveIdx(activeIdx - 1);
    }
  };

  // --- Batch save ---
  const handleSaveAll = async () => {
    const toSave = candidates.filter(c => c._status === 'success' && c.name.trim());
    if (toSave.length === 0) {
      toast.error('没有可保存的候选人（请确认姓名已填写）');
      return;
    }

    setSubmitting(true);
    let savedCount = 0;
    let mergedCount = 0;

    for (let i = 0; i < candidates.length; i++) {
      const c = candidates[i];
      if (c._status !== 'success' || !c.name.trim()) continue;

      setCandidates(prev => {
        const updated = [...prev];
        updated[i] = { ...updated[i], _status: 'saving' };
        return updated;
      });

      try {
        const result = await createCandidate({
          name: c.name,
          phone: c.phone,
          email: c.email,
          gender: c.gender,
          age: c.age ? parseInt(c.age) : 0,
          latest_company: c.latest_company,
          latest_title: c.latest_title,
          work_experiences: c.work_experiences || [],
          project_experiences: c.project_experiences || [],
          education: c.education,
          school: c.school,
          major: c.major,
          work_years: c.work_years ? parseInt(c.work_years) : 0,
          expected_salary: c.expected_salary,
          expected_city: c.expected_city,
          source: c.source,
          portfolio_url: c.portfolio_url,
          position_id: c.position_id || null,
          notes: c.notes,
          resume_file: c.resume_file,
          resume_text: c.resume_text,
        });
        savedCount++;
        if (result._merged) mergedCount++;
        setCandidates(prev => {
          const updated = [...prev];
          updated[i] = { ...updated[i], _status: 'saved' };
          return updated;
        });
      } catch {
        setCandidates(prev => {
          const updated = [...prev];
          updated[i] = { ...updated[i], _status: 'error', _error: '保存失败' };
          return updated;
        });
      }
    }

    setSubmitting(false);
    if (savedCount > 0) {
      const msg = mergedCount > 0
        ? `成功处理 ${savedCount} 位候选人（其中 ${mergedCount} 位为已有记录，已合并更新）`
        : `成功添加 ${savedCount} 位候选人`;
      toast.success(msg);
      onCreated();
    }
  };

  // --- Save single ---
  const handleSaveSingle = async () => {
    const c = candidates[activeIdx];
    if (!c || !c.name.trim()) {
      toast.error('请填写候选人姓名');
      return;
    }

    setSubmitting(true);
    setCandidates(prev => {
      const updated = [...prev];
      updated[activeIdx] = { ...updated[activeIdx], _status: 'saving' };
      return updated;
    });

    try {
      const result = await createCandidate({
        name: c.name,
        phone: c.phone,
        email: c.email,
        gender: c.gender,
        age: c.age ? parseInt(c.age) : 0,
        latest_company: c.latest_company,
        latest_title: c.latest_title,
        work_experiences: c.work_experiences || [],
        project_experiences: c.project_experiences || [],
        education: c.education,
        school: c.school,
        major: c.major,
        work_years: c.work_years ? parseInt(c.work_years) : 0,
        expected_salary: c.expected_salary,
        expected_city: c.expected_city,
        source: c.source,
        portfolio_url: c.portfolio_url,
        position_id: c.position_id || null,
        notes: c.notes,
        resume_file: c.resume_file,
        resume_text: c.resume_text,
      });
      setCandidates(prev => {
        const updated = [...prev];
        updated[activeIdx] = { ...updated[activeIdx], _status: 'saved' };
        return updated;
      });
      toast.success(result._merged ? `${c.name} 已存在，信息已合并更新` : `${c.name} 已添加`);
      onCreated();
    } catch {
      setCandidates(prev => {
        const updated = [...prev];
        updated[activeIdx] = { ...updated[activeIdx], _status: 'error', _error: '保存失败' };
        return updated;
      });
      toast.error('添加失败');
    } finally {
      setSubmitting(false);
    }
  };

  const current = candidates[activeIdx] || null;
  const parsedCount = candidates.filter(c => c._status === 'success').length;
  const parsingCount = candidates.filter(c => c._status === 'parsing').length;
  const savedCount = candidates.filter(c => c._status === 'saved').length;
  const savableCount = candidates.filter(c => c._status === 'success' && c.name.trim()).length;

  const inputCls = 'w-full h-8 px-2.5 rounded border border-gray-300 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500';
  const labelCls = 'block text-[11px] text-gray-500 mb-0.5';

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 shrink-0">
          <div className="flex items-center gap-3">
            {step === 'review' && candidates.length > 0 && (
              <button
                onClick={() => setStep('upload')}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <ChevronLeft size={18} />
              </button>
            )}
            <h3 className="text-base font-semibold text-gray-800">
              {step === 'upload' ? '添加候选人' : `候选人信息确认 (${candidates.length})`}
            </h3>
            {parsingCount > 0 && (
              <span className="flex items-center gap-1 text-xs text-gray-600 bg-gray-50 px-2 py-0.5 rounded-full">
                <Loader2 size={12} className="animate-spin" />
                AI 解析中... ({parsingCount})
              </span>
            )}
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded">
            <X size={18} />
          </button>
        </div>

        {/* Step: Upload */}
        {step === 'upload' && (
          <div className="flex-1 overflow-y-auto p-6">
            {/* Drag & Drop Zone */}
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={clsx(
                'border-2 border-dashed rounded-xl p-12 text-center transition-all cursor-pointer',
                dragOver
                  ? 'border-primary-400 bg-primary-50'
                  : 'border-gray-300 hover:border-primary-300 hover:bg-gray-50'
              )}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx"
                multiple
                onChange={e => { if (e.target.files) handleFiles(e.target.files); e.target.value = ''; }}
                className="hidden"
              />
              <div className="flex flex-col items-center gap-3">
                <div className={clsx(
                  'w-16 h-16 rounded-full flex items-center justify-center transition-colors',
                  dragOver ? 'bg-primary-100' : 'bg-gray-100'
                )}>
                  <Sparkles size={28} className={dragOver ? 'text-primary-500' : 'text-gray-400'} />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    拖放简历文件到这里，或点击选择文件
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    支持 PDF、DOC、DOCX 格式，可多选 · AI 自动解析简历信息
                  </p>
                  <p className="text-xs text-gray-300 mt-0.5">
                    文件名含「作品集」的文件会直接存储，不解析内容
                  </p>
                </div>
              </div>
            </div>

            {/* Already uploaded files summary */}
            {candidates.length > 0 && (
              <div className="mt-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">
                    已上传 {candidates.length} 份简历
                    {parsedCount > 0 && `，${parsedCount} 份解析完成`}
                    {savedCount > 0 && `，${savedCount} 份已保存`}
                  </span>
                  <button
                    onClick={() => { setStep('review'); setActiveIdx(0); }}
                    className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                  >
                    查看并编辑 →
                  </button>
                </div>
                <div className="space-y-1">
                  {candidates.map((c, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-gray-600 px-2 py-1 bg-gray-50 rounded">
                      <FileText size={13} className="text-gray-400 shrink-0" />
                      <span className="truncate flex-1">{c._fileName || c.name || '手动添加'}</span>
                      {c._status === 'parsing' && <Loader2 size={13} className="animate-spin text-gray-500 shrink-0" />}
                      {c._status === 'success' && <CheckCircle2 size={13} className="text-primary-500 shrink-0" />}
                      {c._status === 'saved' && <CheckCircle2 size={13} className="text-primary-500 shrink-0" />}
                      {c._status === 'error' && <AlertCircle size={13} className="text-gray-400 shrink-0" />}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Manual add button */}
            <div className="mt-6 pt-4 border-t border-gray-100 text-center">
              <button
                onClick={handleAddManual}
                className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary-600 transition-colors"
              >
                <UserPlus size={15} />
                不上传简历，直接手动填写
              </button>
            </div>
          </div>
        )}

        {/* Step: Review */}
        {step === 'review' && (
          <div className="flex flex-1 overflow-hidden min-h-0">
            {/* Left sidebar: file list */}
            <div className="w-48 shrink-0 border-r border-gray-200 bg-gray-50 overflow-y-auto">
              <div className="p-2 space-y-0.5">
                {candidates.map((c, i) => (
                  <div
                    key={i}
                    onClick={() => setActiveIdx(i)}
                    className={clsx(
                      'flex items-center gap-1.5 px-2 py-2 rounded text-xs cursor-pointer transition-colors group',
                      activeIdx === i ? 'bg-primary-100 text-primary-700' : 'hover:bg-gray-100 text-gray-600'
                    )}
                  >
                    {c._status === 'parsing' && <Loader2 size={12} className="animate-spin text-gray-500 shrink-0" />}
                    {c._status === 'success' && <FileText size={12} className="text-gray-400 shrink-0" />}
                    {c._status === 'saved' && <CheckCircle2 size={12} className="text-primary-500 shrink-0" />}
                    {c._status === 'saving' && <Loader2 size={12} className="animate-spin text-primary-500 shrink-0" />}
                    {c._status === 'error' && <AlertCircle size={12} className="text-gray-400 shrink-0" />}
                    <span className="truncate flex-1">{c.name || c._fileName || `候选人 ${i + 1}`}</span>
                    {c._status !== 'saved' && c._status !== 'saving' && (
                      <button
                        onClick={e => { e.stopPropagation(); handleRemove(i); }}
                        className="opacity-0 group-hover:opacity-100 p-0.5 text-gray-400 hover:text-gray-500"
                      >
                        <Trash2 size={11} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {/* Add more files */}
              <div className="p-2 border-t border-gray-200">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full flex items-center justify-center gap-1 px-2 py-1.5 text-[11px] text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded transition-colors"
                >
                  <Upload size={11} />
                  继续上传
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx"
                  multiple
                  onChange={e => { if (e.target.files) handleFiles(e.target.files); e.target.value = ''; }}
                  className="hidden"
                />
              </div>
            </div>

            {/* Right: form editor */}
            <div className="flex-1 overflow-y-auto p-4">
              {!current ? (
                <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                  请选择或上传简历
                </div>
              ) : current._status === 'parsing' ? (
                <div className="flex flex-col items-center justify-center h-full gap-3">
                  <Loader2 size={32} className="animate-spin text-primary-500" />
                  <p className="text-sm text-gray-500">AI 正在解析 {current._fileName}...</p>
                  <p className="text-xs text-gray-400">解析完成后将自动填入信息</p>
                </div>
              ) : current._status === 'saved' ? (
                <div className="flex flex-col items-center justify-center h-full gap-3">
                  <CheckCircle2 size={32} className="text-primary-500" />
                  <p className="text-sm text-gray-600">{current.name} 已保存成功</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {current._status === 'error' && current._error && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-xs text-gray-600">
                      <AlertCircle size={14} />
                      {current._error} — 你可以手动填写信息
                    </div>
                  )}

                  {current._fileName && (
                    <div className="flex items-center gap-2 text-xs text-gray-400 mb-1">
                      <FileText size={12} />
                      简历文件: {current._fileName}
                    </div>
                  )}

                  {/* Basic Info */}
                  <div className="grid grid-cols-4 gap-2">
                    <div>
                      <label className={labelCls}>姓名 *</label>
                      <input type="text" value={current.name} onChange={e => handleChange('name', e.target.value)} className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>性别</label>
                      <select value={current.gender} onChange={e => handleChange('gender', e.target.value)} className={inputCls}>
                        <option value="">-</option>
                        <option value="男">男</option>
                        <option value="女">女</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>年龄</label>
                      <input type="number" value={current.age} onChange={e => handleChange('age', e.target.value)} className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>工作年限</label>
                      <input type="number" value={current.work_years} onChange={e => handleChange('work_years', e.target.value)} className={inputCls} />
                    </div>
                  </div>

                  {/* Contact */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className={labelCls}>手机号</label>
                      <input type="tel" value={current.phone} onChange={e => handleChange('phone', e.target.value)} className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>邮箱</label>
                      <input type="email" value={current.email} onChange={e => handleChange('email', e.target.value)} className={inputCls} />
                    </div>
                  </div>

                  {/* Work */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className={labelCls}>最近公司</label>
                      <input type="text" value={current.latest_company} onChange={e => handleChange('latest_company', e.target.value)} className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>最近职位</label>
                      <input type="text" value={current.latest_title} onChange={e => handleChange('latest_title', e.target.value)} className={inputCls} />
                    </div>
                  </div>

                  {/* Education */}
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className={labelCls}>学历</label>
                      <select value={current.education} onChange={e => handleChange('education', e.target.value)} className={inputCls}>
                        <option value="">-</option>
                        {EDUCATION_OPTIONS.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>学校</label>
                      <input type="text" value={current.school} onChange={e => handleChange('school', e.target.value)} className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>专业</label>
                      <input type="text" value={current.major} onChange={e => handleChange('major', e.target.value)} className={inputCls} />
                    </div>
                  </div>

                  {/* Expectation */}
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className={labelCls}>期望薪资</label>
                      <input type="text" value={current.expected_salary} onChange={e => handleChange('expected_salary', e.target.value)} placeholder="如 25-35K" className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>期望城市</label>
                      <input type="text" value={current.expected_city} onChange={e => handleChange('expected_city', e.target.value)} className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>来源渠道</label>
                      <select value={current.source} onChange={e => handleChange('source', e.target.value)} className={inputCls}>
                        <option value="">-</option>
                        {SOURCE_OPTIONS.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Portfolio */}
                  <div>
                    <label className={labelCls}>作品集链接</label>
                    {current.portfolio_url && current.portfolio_url.startsWith('/uploads/') ? (
                      <div className="flex items-center gap-2">
                        <div className="flex-1 flex items-center gap-1.5 h-8 px-2.5 rounded border border-primary-300 bg-primary-50 text-sm text-primary-700">
                          <CheckCircle2 size={13} className="shrink-0" />
                          <span className="truncate">作品集已上传</span>
                        </div>
                        <button onClick={() => handleChange('portfolio_url', '')} className="text-xs text-gray-400 hover:text-gray-500">清除</button>
                      </div>
                    ) : (
                      <input type="url" value={current.portfolio_url} onChange={e => handleChange('portfolio_url', e.target.value)} placeholder="如 Behance / Dribbble / 个人网站" className={inputCls} />
                    )}
                  </div>

                  {/* Position */}
                  <div>
                    <label className={labelCls}>应聘岗位</label>
                    <select value={current.position_id} onChange={e => handleChange('position_id', e.target.value)} className={inputCls}>
                      <option value="">请选择岗位</option>
                      {positions.filter(p => p.status === 'open').map(p => (
                        <option key={p.id} value={p.id}>{p.name}{p.department ? ` - ${p.department}` : ''}</option>
                      ))}
                    </select>
                  </div>

                  {/* Notes */}
                  <div>
                    <label className={labelCls}>备注 / AI摘要</label>
                    <textarea
                      value={current.notes}
                      onChange={e => handleChange('notes', e.target.value)}
                      rows={3}
                      className="w-full rounded border border-gray-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 resize-none"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 shrink-0">
          <div className="text-xs text-gray-400">
            {step === 'review' && candidates.length > 1 && (
              <span>
                {activeIdx + 1} / {candidates.length}
                {savableCount > 0 && ` · ${savableCount} 待保存`}
                {savedCount > 0 && ` · ${savedCount} 已保存`}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 rounded transition-colors"
            >
              关闭
            </button>
            {step === 'review' && candidates.length > 0 && (
              <>
                {/* Nav prev/next */}
                {candidates.length > 1 && (
                  <div className="flex items-center gap-1 mr-1">
                    <button
                      onClick={() => setActiveIdx(Math.max(0, activeIdx - 1))}
                      disabled={activeIdx === 0}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 rounded"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <button
                      onClick={() => setActiveIdx(Math.min(candidates.length - 1, activeIdx + 1))}
                      disabled={activeIdx === candidates.length - 1}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 rounded"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                )}

                {/* Save single */}
                {current && current._status === 'success' && (
                  <button
                    onClick={handleSaveSingle}
                    disabled={submitting || !current.name.trim()}
                    className="px-3 py-1.5 text-sm text-primary-600 border border-primary-200 hover:bg-primary-50 rounded-md disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1 transition-colors"
                  >
                    {submitting && <Loader2 size={13} className="animate-spin" />}
                    保存当前
                  </button>
                )}

                {/* Batch save */}
                {savableCount > 1 && (
                  <button
                    onClick={handleSaveAll}
                    disabled={submitting || parsingCount > 0}
                    className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1 transition-colors"
                  >
                    {submitting && <Loader2 size={13} className="animate-spin" />}
                    全部保存 ({savableCount})
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
